import React, { useEffect, useState, useRef } from 'react';
import { Container, Carousel, Card, Button, Row, Col, Badge } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../config/api';
import { usePageLoader } from '../context/PageLoaderContext';
import ProductCard from '../components/product/ProductCard';
import ComboCard from '../components/product/ComboCard';
import CachedImage from '../components/CachedImage';
import './Home.css';
import productImg from '../assets/me.png';
import { convertToFirebaseApiUrl } from '../hooks/useCachedImageUrl';
/** Build products page URL from banner category link params (supports L1, L2, L3). */
const getBannerProductsUrl = (banner) => {
  if (!banner?.category_id) return null;
  const cat = banner.link_category_id != null ? banner.link_category_id : banner.category_id;
  const sub = banner.link_subcategory_id ?? null;
  const l3 = banner.link_l3_category_id ?? null;
  const params = new URLSearchParams();
  params.set('category', String(cat));
  if (sub) params.set('subcategory', String(sub));
  if (l3) params.set('l3', String(l3));
  return `/products?${params.toString()}`;
};

const Home = () => {
  const { setPageLoading } = usePageLoader();
  const [banners, setBanners] = useState([]);
  const [reels, setReels] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showcaseCategories, setShowcaseCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState(0);
  const [categoryImageLoading, setCategoryImageLoading] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setPageLoading(loading);
    return () => setPageLoading(false);
  }, [loading, setPageLoading]);
  const categoryButtonsListRef = useRef(null);
  const [announcementIndex, setAnnouncementIndex] = useState(0);
  const [showAnnouncement, setShowAnnouncement] = useState(true);
  const [curatedIndex, setCuratedIndex] = useState(0);
  const [collections, setCollections] = useState([]);
  const [combos, setCombos] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [categoryScrollPosition, setCategoryScrollPosition] = useState(400);
  const navigate = useNavigate();
  // Reset curatedIndex when collections change
  useEffect(() => {
    if (collections.length > 0 && curatedIndex >= collections.length) {
      setCuratedIndex(0);
    }
  }, [collections, curatedIndex]); // Start with 3rd card (index 2) in center: 2 * 200 = 400
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const categoryCardsRef = useRef(null);
  const autoScrollTimeoutRef = useRef(null);
  const lastInteractionTimeRef = useRef(Date.now());
  const spotlightsCarouselRef = useRef(null);
  const categorySectionRef = useRef(null);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const mouseStartX = useRef(null);
  const mouseStartY = useRef(null);
  const isDragging = useRef(false);


  useEffect(() => {
    fetchData();
    // Rotate announcements every 5 seconds
  }, []);

  // Auto-scroll category list on mobile when selection changes
  useEffect(() => {
    if (categoryButtonsListRef.current && window.innerWidth <= 991) {
      const selectedButton = categoryButtonsListRef.current.children[selectedCategoryIndex];
      if (selectedButton) {
        selectedButton.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }
  }, [selectedCategoryIndex]);

  // Swipe handlers for mobile (touch events)
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    // Allow default scrolling behavior
  };

  const handleTouchEnd = (e) => {
    if (!touchStartX.current || !touchStartY.current || showcaseCategories.length === 0) return;

    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    const diffX = touchStartX.current - touchEndX;
    const diffY = touchStartY.current - touchEndY;

    // Only process swipe if horizontal movement is greater than vertical (swipe, not scroll)
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
      if (diffX > 0) {
        // Swipe left - next category
        if (selectedCategoryIndex < showcaseCategories.length - 1) {
          setSelectedCategoryIndex(selectedCategoryIndex + 1);
        } else {
          setSelectedCategoryIndex(0);
        }
      } else {
        // Swipe right - previous category
        if (selectedCategoryIndex > 0) {
          setSelectedCategoryIndex(selectedCategoryIndex - 1);
        } else {
          setSelectedCategoryIndex(showcaseCategories.length - 1);
        }
      }
    }

    touchStartX.current = null;
    touchStartY.current = null;
  };

  // Swipe handlers for desktop (mouse events)
  const handleMouseDown = (e) => {
    isDragging.current = true;
    mouseStartX.current = e.clientX;
    mouseStartY.current = e.clientY;
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    // Prevent text selection while dragging
    e.preventDefault();
  };

  const handleMouseUp = (e) => {
    if (!isDragging.current || !mouseStartX.current || !mouseStartY.current || showcaseCategories.length === 0) {
      isDragging.current = false;
      return;
    }

    const mouseEndX = e.clientX;
    const mouseEndY = e.clientY;
    const diffX = mouseStartX.current - mouseEndX;
    const diffY = mouseStartY.current - mouseEndY;

    // Only process swipe if horizontal movement is greater than vertical and significant
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
      if (diffX > 0) {
        // Swipe left - next category
        if (selectedCategoryIndex < showcaseCategories.length - 1) {
          setSelectedCategoryIndex(selectedCategoryIndex + 1);
        } else {
          setSelectedCategoryIndex(0);
        }
      } else {
        // Swipe right - previous category
        if (selectedCategoryIndex > 0) {
          setSelectedCategoryIndex(selectedCategoryIndex - 1);
        } else {
          setSelectedCategoryIndex(showcaseCategories.length - 1);
        }
      }
    }

    isDragging.current = false;
    mouseStartX.current = null;
    mouseStartY.current = null;
  };

  // Handle mouse leave to reset drag state
  const handleMouseLeave = () => {
    isDragging.current = false;
    mouseStartX.current = null;
    mouseStartY.current = null;
  };

  // Auto-scroll category cards
  useEffect(() => {
    if (!isAutoScrolling) return;

    const interval = setInterval(() => {
      setCategoryScrollPosition((prev) => {
        const cardHeight = 200; // min-height (180px) + gap (20px)
        const totalCards = 5;
        const maxScroll = cardHeight * totalCards;
        const newPosition = prev + cardHeight;
        
        if (newPosition >= maxScroll) {
          return 0; // Reset to start for seamless loop
        }
        return newPosition;
      });
    }, 3000); // Move every 3 seconds

    return () => clearInterval(interval);
  }, [isAutoScrolling]);

  // Resume auto-scroll after 3 seconds of inactivity
  useEffect(() => {
    if (isAutoScrolling) return; // Don't set timeout if already auto-scrolling

    if (autoScrollTimeoutRef.current) {
      clearTimeout(autoScrollTimeoutRef.current);
    }

    autoScrollTimeoutRef.current = setTimeout(() => {
      setIsAutoScrolling(true);
    }, 3000);

    return () => {
      if (autoScrollTimeoutRef.current) {
        clearTimeout(autoScrollTimeoutRef.current);
      }
    };
  }, [isAutoScrolling, categoryScrollPosition]);

  const handleCategoryWheel = (e) => {
    e.preventDefault();
    setIsAutoScrolling(false);
    lastInteractionTimeRef.current = Date.now();

    const delta = e.deltaY > 0 ? 200 : -200; // Card height + gap
    setCategoryScrollPosition((prev) => {
      const cardHeight = 200;
      const totalCards = 5;
      const maxScroll = cardHeight * totalCards;
      let newPosition = prev + delta;

      if (newPosition < 0) {
        newPosition = maxScroll - cardHeight;
      } else if (newPosition >= maxScroll) {
        newPosition = 0;
      }

      return newPosition;
    });
  };
  const handleShowMore = () => {
    navigate('/products');
  };

  const handleSelectCategory = (index) => {
    setSelectedCategoryIndex(index);
    setCategoryImageLoading(true);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [bannersRes, reelsRes, productsRes, categoriesRes, showcaseRes, collectionsRes, combosRes, testimonialsRes] =
        await Promise.allSettled([
        api.get('/banners?is_published=true&is_sales=false'),
        api.get('/reels?is_published=true'),
        api.get('/products/bestsellers'),
        api.get('/categories?is_active=true'),
          api.get('/categories/showcase'),
          api.get('/collections?is_active=true'),
          api.get('/combos?is_active=true&limit=8'),
          api.get('/products/happy-clients/testimonials'),
        ]);

      const homeBanners = bannersRes.status === 'fulfilled' ? bannersRes.value.banners || [] : [];
      setBanners(homeBanners.filter((b) => !(b.is_sales === 1 || b.is_sales === true)));
      setReels(reelsRes.status === 'fulfilled' ? reelsRes.value.reels || [] : []);
      const products = productsRes.status === 'fulfilled' ? productsRes.value.products || [] : [];
      setAllProducts(products);

      const categories =
        categoriesRes.status === 'fulfilled' ? categoriesRes.value.categories || [] : [];
      setCategories(categories);

      // Set showcase categories
      let showcase = [];
      if (showcaseRes.status === 'fulfilled') {
        showcase = showcaseRes.value.categories || [];
        console.log('Showcase categories fetched:', showcase);
        console.log('Showcase response:', showcaseRes.value);
      } else {
        console.error('Error fetching showcase categories:', showcaseRes.reason);
        console.error('Full error:', showcaseRes);
      }

      // Flatten all categories for the category list
      const flattenCategories = (cats) => {
        let result = [];
        cats.forEach((cat) => {
          result.push(cat);
          if (cat.children && cat.children.length > 0) {
            result = result.concat(flattenCategories(cat.children));
          }
        });
        return result;
      };
      const allCats = flattenCategories(categories);

      // If no showcase categories, use first 3 active categories as fallback
      // if (showcase.length === 0 && allCats.length > 0) {
      //   showcase = allCats.slice(0, Math.min(3, allCats.length));
      //   console.log('No showcase categories found, using fallback:', showcase);
      // }

      console.log('Final showcase categories:', showcase);
      setShowcaseCategories(showcase);

      setAllCategories(allCats);
      console.log('All categories:', allCats);

      // Set collections
      let collectionsData = [];
      if (collectionsRes.status === 'fulfilled') {
        collectionsData = collectionsRes.value.collections || [];
        console.log('Collections response:', collectionsRes.value);
        console.log('Collections fetched:', collectionsData);
      } else {
        console.error('Error fetching collections:', collectionsRes.reason);
      }
      setCollections(collectionsData);

      if (combosRes.status === 'fulfilled') {
        setCombos(combosRes.value.combos || []);
      } else {
        console.error('Error fetching combos:', combosRes.reason);
      }

      // Happy Clients testimonials (admin comments from products)
      const testimonialsData = testimonialsRes.status === 'fulfilled' ? (testimonialsRes.value.testimonials || []) : [];
      setTestimonials(testimonialsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };


  if (loading) return null;

  return (
    <div className="home-page">
      {/* Hero Section */}
      {banners.length > 0 && (
        <section className="hero-section">
          <Carousel className="hero-carousel" indicators interval={2000} fade>
            {banners.map((banner, index) => (
              <Carousel.Item
                onClick={(e) => {
                  const url = getBannerProductsUrl(banner);
                  if (url) {
                    navigate(url);
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                key={banner.id || index}
              >
                <div className="hero-slide">
                  <CachedImage
                    className="hero-image"
                    src={banner.image_url}
                    alt={banner.title || 'Banner'}
                  />
                  {banner.category_id && (
                    <div
                      className="hero-overlay hero-overlay--full"
                      role="presentation"
                    >
                      <div
                        className="hero-cta-wrap"
                        onClick={(e) => { const u = getBannerProductsUrl(banner); if (u) navigate(u); }}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <Link
                          to={getBannerProductsUrl(banner) || '/products'}
                          className="hero-cta"
                          onClick={(e) => { e.stopPropagation(); }}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          SHOP NOW
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
            </Carousel.Item>
          ))}
        </Carousel>
        </section>
      )}
      {/* Curated Collections Section */}
      <section className="curated-collections">
        <Container>
          <h2 className="section-title mb-8 lg:mb-10 lg:mt-2">Signature picks</h2>
          {collections.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No collections available at the moment.</p>
            </div>
          ) : (
          <Carousel
            className="curated-carousel"
            indicators={false}
            interval={null}
            touch={true}
            wrap={true}
            controls={false}
            activeIndex={curatedIndex}
            onSelect={(selectedIndex) => setCuratedIndex(selectedIndex)}
          >
              {collections.map((collection, collectionIndex) => (
                <Carousel.Item key={collection.id}>
              <div className="curated-content-wrapper">
                {/* Left Side - All Cards */}
                <div className="curated-cards-wrapper">
                  <div className="curated-cards-flex">
                        {/* Row 1 - Collection Info and Starting At */}
                    <div className="curated-top-row">
                          {/* Collection Card */}
                      <div className="earthy-drip-section">
                        <Card className="collection-card earthy-drip">
                          <Card.Body>
                                <p className="collection-label">{collection.collection_label || 'FREAKERS FAMILY'}</p>
                                <h3 className="collection-name">{collection.collection_name || 'Collection'}</h3>
                                {collection.colors && collection.colors.length > 0 && (
                            <div className="color-swatches">
                                    {collection.colors.slice(0, 3).map((color, idx) => (
                                      <div
                                        key={idx}
                                        className="swatch"
                                        style={{ backgroundColor: color.hex_code }}
                                      ></div>
                                    ))}
                            </div>
                                )}
                          </Card.Body>
                        </Card>
                      </div>

                      {/* Starting At Card */}
                      <div className="sold-out-section">
                            {collection.starting_at && (
                        <Card className="collection-card sold-out">
                          <Card.Body>
                                  <h3 className="sold-out-title">Starting at</h3>
                                  <p className="sold-out-number">₹{Math.round(parseFloat(collection.starting_at) || 0)}</p>
                                  <p className="sold-out-text">Only</p>
                                </Card.Body>
                              </Card>
                            )}
                      </div>
                    </div>

                    {/* Row 2 - Tags and Sold Out */}
                    {((collection.tags && collection.tags.length > 0) || (collection.soldout_count > 0)) && (
                    <div className="curated-top-row curated-second-row">
                          {/* Tags Card */}
                      <div className="earthy-drip-section" id="tags-section">
                            {collection.tags && collection.tags.length > 0 && (
                        <Card className="collection-card">
                          <Card.Body>
                            <div className="collection-tags">
                                    {collection.tags.map((tag, idx) => (
                                      <span key={idx} className="tag">
                                      {tag}
                              </span>
                                    ))}
                            </div>
                          </Card.Body>
                        </Card>
                            )}
                      </div>

                      {/* Sold Out Card */}
                      <div className="sold-out-section">
                            {collection.soldout_count > 0 && (
                              <Card className="collection-card sold-out">
                                <Card.Body>
                            <h3 className="sold-out-title">Sold Out</h3>
                                  <p className="sold-out-number">+{collection.soldout_count}</p>
                            <p className="sold-out-text">In Past 30 Days</p>
                          </Card.Body>
                        </Card>
                            )}
                      </div>
                    </div>
                    )}

                    {/* Product Preview Cards - Full Width Row with 2 Products */}
                        {collection.products && collection.products.length > 0 && (
                    <div className="product-preview-section">
                      <Card className="collection-card w-full">
                        <div className="product-preview-container">
                                {collection.products.slice(0, 2).map((product, index) => (
                            <div
                              key={product.id}
                              className={`product-preview-card max-w-[150px] relative ${
                                window.innerWidth <= 991 ?  '' : index === 0 ? '-rotate-6' : 'rotate-6'
                              }`}
                            >
                              {/* Heart */}
                              <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10 bg-black p-2 rounded-full">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="20"
                                  height="21"
                                  viewBox="0 0 20 21"
                                  fill="none"
                                >
                                  <path
                                    d="M11.1982 20.2482C5.60602 17.0487 2.49592 13.8601 1.04178 11.03C-2.85412 3.44552 5.14343 -1.56744 9.1357 2.67199C12.0196 -2.53383 20.9151 0.104977 18.8967 8.52066C18.1431 11.661 15.8701 15.6062 11.1982 20.2482Z"
                                    fill="white"
                                  />
                                </svg>
                              </div>

                              <Link to={`/products/${product.id}`} className="text-decoration-none">
                                <div className="bg-white rounded-2xl p-3 shadow-xl relative">
                                  <div className="overflow-hidden rounded-t-xl relative w-full aspect-[3/4]">
                                    <img
                                      src={product.primary_image || '/placeholder.png'}
                                      alt={product.name}
                                      className="w-full h-full object-cover"
                                    />
                                 
                                  </div>

                                  <div className="bg-black text-white text-center py-3 font-semibold tracking-wide rounded-b-xl">
                                    SHOP NOW
                                  </div>
                                </div>
                              </Link>
                            </div>
                          ))}
                        </div>
                      </Card>
                    </div>
                        )}
                  </div>
                </div>

                {/* Elevated Image - Right Side */}
                <div className="elevated-image-wrapper">
                  <div className="elevated-image">
                        <img
                          src={convertToFirebaseApiUrl(collection.collection_image || productImg)}
                          alt={collection.collection_name || 'Featured Model'}
                        />
                        {collection.tag && (
                          <Badge className="bestseller-badge">{collection.tag}</Badge>
                        )}
                    <div className="featured-navigation">
                      <button
                        className="nav-arrow nav-arrow-left"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                              setCuratedIndex((prev) => (prev - 1 + collections.length) % collections.length);
                        }}
                      >
                        ‹
                      </button>
                      <button
                        className="nav-arrow nav-arrow-right"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                              setCuratedIndex((prev) => (prev + 1) % collections.length);
                        }}
                      >
                        ›
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Carousel.Item>
              ))}
          </Carousel>
          )}
        </Container>
      </section>

      {/* Shop Our Combos Section */}
      {combos.length > 0 && (
        <section className="shop-combos-section">
          <Container>
            <h2 className="section-title mb-8 lg:mb-10">Shop Our Combos</h2>
            <Row className="g-4">
              {combos.slice(0, 4).map((combo) => (
                <Col key={combo.id} xs={6} md={4} lg={3}>
                  <ComboCard combo={combo} />
                </Col>
              ))}
            </Row>
            <div className="shop-combos-view-all">
              <Link to="/products?filter=combo" className="load-more-btn text-decoration-none">
                View All Combos
              </Link>
            </div>
          </Container>
        </section>
      )}

       {/* Spotlights Section */}
        {reels.length > 0 && (
        <>
          <div className="section-title text-black mb-8  mt-8 md:mt-0">Spotlights</div>
          <section className="spotlights-section bg-black">
            <Container fluid="sm" className="p-0">
              <div className="spotlights-wrapper">
                <button
                  className="spotlights-nav-arrow spotlights-nav-arrow-left"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (spotlightsCarouselRef.current) {
                      const cardWidth = spotlightsCarouselRef.current.querySelector('.spotlight-card')?.offsetWidth || 350;
                      const gap = 24;
                      const scrollAmount = cardWidth + gap;
                      spotlightsCarouselRef.current.scrollBy({
                        left: -scrollAmount,
                        behavior: 'smooth'
                      });
                    }
                  }}
                >
                  ‹
                </button>
                <div className="spotlights-carousel-container" ref={spotlightsCarouselRef}>
                  <div className="spotlights-cards-row gap-4 px-4">
                    {reels.map((reel) => (
                      <Card 
                        key={reel.id} 
                        className="spotlight-card"
                        onClick={() => {
                          console.log('clicked', reel)
                          if (reel.product_id) {
                            navigate(`/products/${reel.product_id}`);
                          }
                        }}
                        style={{ cursor: reel.product_id ? 'pointer' : 'default' }}
                      >
                        <div className="spotlight-image-wrapper">
                          <div className="spotlight-image">
                        <video
                          src={convertToFirebaseApiUrl(reel.video_url)}
                              autoPlay
                              loop
                        muted
                        playsInline
                              className="spotlight-video"
                            />
                          </div>
                        </div>
                        <Card.Body className="spotlight-card-body">
                          <div className="spotlight-footer">
                            {reel.product_id ? (
                              <Button
                                className="spotlight-view-product-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/products/${reel.product_id}`);
                                }}
                              >
                                VIEW PRODUCT
                              </Button>
                            ) : (
                              <Button
                                className="spotlight-view-product-btn"
                                disabled
                              >
                                VIEW PRODUCT
                              </Button>
                            )}
                          </div>
                      </Card.Body>
                  </Card>
                    ))}
                  </div>
                </div>
                <button
                  className="spotlights-nav-arrow spotlights-nav-arrow-right"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (spotlightsCarouselRef.current) {
                      const cardWidth = spotlightsCarouselRef.current.querySelector('.spotlight-card')?.offsetWidth || 350;
                      const gap = 24;
                      const scrollAmount = cardWidth + gap;
                      spotlightsCarouselRef.current.scrollBy({
                        left: scrollAmount,
                        behavior: 'smooth'
                      });
                    }
                  }}
                >
                  ›
                </button>
              </div>
            </Container>
          </section>
        </>
      )}
           {/* Style By Category Section */}
           {showcaseCategories.length > 0 && (
      <section 
        className="style-by-category"
        ref={categorySectionRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
      >
                  <h2 className="section-title">Style By Category</h2>
        <Container fluid="md" className="category-wrapper">
          <Row className="g-4 category-wrapper-row">
            <Col lg={4} sm={12} className="category-image-col lg:ml-6">
              <div className="category-main-image">
                {categoryImageLoading && (
                  <div className="category-main-image-skeleton" aria-hidden="true" />
                )}
                <CachedImage
                  key={selectedCategoryIndex}
                  src={
                    showcaseCategories[selectedCategoryIndex]?.image ||
                    showcaseCategories[selectedCategoryIndex]?.image_url ||
                    '/placeholder.png'
                  }
                  alt={showcaseCategories[selectedCategoryIndex]?.name || 'Style Category'}
                  onLoad={() => setCategoryImageLoading(false)}
                  onError={() => setCategoryImageLoading(false)}
                  className={categoryImageLoading ? 'category-main-img-loading' : ''}
                />
              </div>
            </Col>
            <Col lg={6} sm={12} className="category-content-col">
              <div className="category-right-section">
                {/* Category Selection Buttons - Below the main card */}
                <div className="category-buttons-wrapper flex flex-row items-center gap-4">
                  {/* Mobile Left Arrow */}
                  <button
                    className="category-nav-btn category-nav-left-mobile"
                    onClick={() => {
                      if (selectedCategoryIndex > 0) {
                        handleSelectCategory(selectedCategoryIndex - 1);
                      } else if (showcaseCategories.length > 0) {
                        handleSelectCategory(showcaseCategories.length - 1);
                      }
                    }}
                    disabled={showcaseCategories.length === 0}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M15 18l-6-6 6-6" />
                    </svg>
                  </button>

                  {/* Desktop Scroll Arrows */}
                  <div className="category-nav-arrows">
                    <button
                      className="category-nav-btn category-nav-up"
                      onClick={() => {
                        if (selectedCategoryIndex > 0) {
                          handleSelectCategory(selectedCategoryIndex - 1);
                        } else if (showcaseCategories.length > 0) {
                          handleSelectCategory(showcaseCategories.length - 1);
                        }
                      }}
                      disabled={showcaseCategories.length === 0}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M18 15l-6-6-6 6" />
                      </svg>
                    </button>
                    <button
                      className="category-nav-btn category-nav-down"
                      onClick={() => {
                        if (selectedCategoryIndex < showcaseCategories.length - 1) {
                          handleSelectCategory(selectedCategoryIndex + 1);
                        } else if (showcaseCategories.length > 0) {
                          handleSelectCategory(0);
                        }
                      }}
                      disabled={showcaseCategories.length === 0}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </button>
                  </div>

                  <div className="category-buttons-list" ref={categoryButtonsListRef}>
                    {showcaseCategories.length > 0 ? (
                      showcaseCategories.map((category, index) => {
                        const isSelected = index === selectedCategoryIndex;
                        return (
                          <div
                            key={category.id}
                            className={`category-select-btn ${
                              index % 2 === 0 ? 'category-btn-green' : 'category-btn-pink'
                            } ${isSelected ? 'category-btn-selected' : ''}`}
                            onClick={() => {
                              handleSelectCategory(index);
                            }}
                          >
                            {!isSelected && <span className="category-plus-icon"><svg xmlns="http://www.w3.org/2000/svg" width="11" height="12" viewBox="0 0 11 12" fill="none">
<path d="M4.10403 11.112V4.48227e-05H6.45603V11.112H4.10403ZM2.74181e-05 6.69605V4.44005H10.512V6.69605H2.74181e-05Z" fill="#B89354"/>
</svg></span>}

                            {!isSelected && <span className="category-btn-name">{category.name}</span>} 
                            {isSelected && (
                              <div >
                                <h3 className="showcase-category-title">
                                  {category.name}
                                </h3>
                                {category.description && (
                                  <p className="showcase-category-description">
                                    {category.description}
                                  </p>
                                )}
                                <Button
                                  as={Link}
                                  to={`/products?category=${category.id}`}
                                  className="showcase-shop-btn"
                                >
                                  SHOP NOW
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : allCategories.length > 0 ? (
                      allCategories.slice(0, 3).map((category, index) => {
                        return (
                          <button
                            key={category.id}
                            className={`category-select-btn ${
                              index % 2 === 0 ? 'category-btn-green' : 'category-btn-pink'
                            }`}
                            onClick={() => {
                              window.location.href = `${process.env.PUBLIC_URL || ''}/products?category=${category.id}`;
                            }}
                          >
                            <span className="category-plus-icon">+</span>
                            <span className="category-btn-name">{category.name}</span>
                          </button>
                        );
                      })
                    ) : (
                      <div className="no-categories-message">
                        <p>No categories available</p>
                      </div>
                    )}
                  </div>

                  {/* Mobile Right Arrow */}
                  <button
                    className="category-nav-btn category-nav-right-mobile"
                    onClick={() => {
                      if (selectedCategoryIndex < showcaseCategories.length - 1) {
                        handleSelectCategory(selectedCategoryIndex + 1);
                      } else if (showcaseCategories.length > 0) {
                        handleSelectCategory(0);
                      }
                    }}
                    disabled={showcaseCategories.length === 0}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M9 18l6-6-6-6" />
                    </svg>
                  </button>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
           )}



     



      {/* Product Listings Section */}
       <h2 className="section-title mb-4 lg:mb-0 lg:mt-4">Bestsellers</h2>
      <section className="product-listings">
        
        <Container>
          <Row className="g-4">
            {allProducts.map((product) => (
              <Col key={product.id} xs={6} md={4} lg={3}>
                <ProductCard product={product} />
              </Col>
            ))}
          </Row>
          {allProducts.length === 0 && (
            <div className="text-center py-5">
              <p className="text-muted">No products available at the moment.</p>
            </div>
          )}
        </Container>
      </section>
      <div className="w-100 flex align-items-center justify-content-center">

      <button onClick={handleShowMore} className="load-more-btn">Show More</button>
      </div>
      {/* Happy Clients Section */}
      <section className="happy-clients">
        <Container>
          <h2 className="section-title mb-8 lg:mb-16 lg:mt-4">Happy Clients</h2>
          <div className="testimonial-card-wrapper">
            <Carousel
              prevIcon={
                <span className="carousel-control-icon">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15 18L9 12L15 6"
                      stroke="#1F1F1F"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              }
              nextIcon={
                <span className="carousel-control-icon">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 18L15 12L9 6"
                      stroke="#1F1F1F"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              }
            >
              {(testimonials.length > 0 ? testimonials : [
                { admin_comment_name: 'JESSICA N', admin_comment_text: 'I Absolutely Love Freakers The Products Are High-Quality And The Customer Service Is Excellent. I Always Leave With Exactly What I Need And A Smile On My Face.' },
                { admin_comment_name: 'MICHAEL R', admin_comment_text: 'The Quality And Style Of Products At Freakers Is Unmatched. Fast Shipping And Great Packaging Make Every Purchase A Delight. Highly Recommend To Everyone!' },
                { admin_comment_name: 'SARAH K', admin_comment_text: 'Best Shopping Experience Ever! The Website Is Easy To Navigate, Products Are Exactly As Described, And The Return Process Was Seamless. Will Definitely Shop Again!' },
                { admin_comment_name: 'DAVID L', admin_comment_text: 'Freakers Has Become My Go-To Store For All Fashion Needs. The Variety Is Amazing, Prices Are Fair, And Customer Support Team Is Always Helpful. Five Stars!' },
              ]).map((t, idx) => (
              <Carousel.Item key={t.id != null ? t.id : idx}>
                <Card className="testimonial-card">
                  <Card.Body>
                    <div className="testimonial-content">
                      
                        <p className="testimonial-text">
                          "{typeof t.admin_comment_text === 'string' ? t.admin_comment_text : ''}"
                        </p>
                           <h4 className="testimonial-name">- {t.admin_comment_name || 'Happy Customer'}</h4>
                     
                    </div>
                  </Card.Body>
                </Card>
              </Carousel.Item>
              ))}
            </Carousel>
          </div>
        </Container>
        </section>
    </div>
  );
};

export default Home;
